import React, { useState, useEffect, useRef, useCallback } from "react";
import { polygonCluster, polygonWsSymbol } from "../utils/polygonSymbol";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageContainer from "../components/pages/PageContainer";
import PageHeaderTitle from "../components/pages/PageHeaderTitle";
import ChartComp from "../components/chart/Chart";
import { getCandlestickData } from "../utils/api";
import { toast } from "react-toastify";
import { SkeletonChart } from "../components/Skeleton/Skeleton";

const useLiveChartData = (tickerFromLocalStorage, setting, initialData) => {
  const [liveData, setLiveData] = useState(initialData || []);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [previousPrice, setPreviousPrice] = useState(null);
  const wsRef = useRef(null);
  const lastUpdateTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const apiKey = process.env.REACT_APP_API_KEY;

  const getTimeInterval = (period, multiplier = 1) => {
    const baseIntervals = {
      minute: 60000,
      hour: 3600000,
      day: 86400000,
      week: 604800000,
      month: 2592000000,
      "1m": 60000,
      "2m": 120000,
      "5m": 300000,
      "15m": 900000,
      "30m": 1800000,
      "1h": 3600000,
      "4h": 14400000,
      "1D": 86400000,
      "1W": 604800000,
      "1M": 2592000000,
    };

    if (["minute", "hour"].includes(period)) {
      return (baseIntervals[period] || 60000) * multiplier;
    }

    if (["day", "week", "month"].includes(period)) {
      return baseIntervals[period] || 86400000;
    }

    return baseIntervals[period] || 60000;
  };

  const roundToInterval = (timestamp, interval) => {
    return Math.floor(timestamp / interval) * interval;
  };

  const createNewBar = useCallback(
    (currentTime, lastClose) => {
      const interval = getTimeInterval(
        setting?.period || "1m",
        setting?.multiplier || 1,
      );
      const roundedTime = roundToInterval(currentTime, interval);

      return {
        key: Date.now(),
        date: new Date(roundedTime),
        open: lastClose,
        high: lastClose,
        low: lastClose,
        close: lastClose,
        volume: 0,
        chart: [],
        indicators: [],
      };
    },
    [setting?.period],
  );

  const updateLiveData = useCallback(
    (newPrice, volume = 0) => {
      if (!liveData || !Array.isArray(liveData) || liveData.length === 0) {
        return;
      }

      setPreviousPrice(currentPrice);
      setCurrentPrice(newPrice);

      const period = setting?.period || "1m";
      const isLongTimeframe = [
        "1h",
        "4h",
        "1D",
        "1W",
        "1M",
        "hour",
        "day",
        "week",
        "month",
      ].includes(period);

      if (isLongTimeframe) {
        setLiveData((prevData) => {
          if (!prevData.length) return prevData;
          const newData = [...prevData];
          const currentBar = { ...newData[newData.length - 1] };
          currentBar.close = newPrice;

          const openPrice = currentBar.open ?? currentBar.close;
          const deltaFromOpen =
            Math.abs(newPrice - openPrice) / (openPrice || 1);
          const maxDelta = 0.02;

          if (newPrice > currentBar.high && deltaFromOpen < maxDelta) {
            currentBar.high = newPrice;
          }
          if (newPrice < currentBar.low && deltaFromOpen < maxDelta) {
            currentBar.low = newPrice;
          }

          currentBar.volume += volume;
          newData[newData.length - 1] = currentBar;
          return newData;
        });

        lastUpdateTimeRef.current = Date.now();
        return;
      }

      const now = new Date();
      const currentTime = now.getTime();
      const multiplier = setting?.multiplier || 1;
      const interval = getTimeInterval(period, multiplier);
      const roundedTime = roundToInterval(currentTime, interval);

      setLiveData((prevData) => {
        const newData = [...prevData];
        const lastDataPoint = newData[newData.length - 1];
        const lastDataTime = new Date(lastDataPoint.date).getTime();
        const lastRoundedTime = roundToInterval(lastDataTime, interval);

        if (roundedTime === lastRoundedTime) {
          const currentBar = { ...newData[newData.length - 1] };

          currentBar.close = newPrice;
          currentBar.high = Math.max(currentBar.high, newPrice);
          currentBar.low = Math.min(currentBar.low, newPrice);
          if (currentBar.high === currentBar.low) {
            const basePrice = currentBar.open;
            const movement = basePrice * 0.001; // 0.1% movement
            currentBar.high = Math.max(currentBar.high, basePrice + movement);
            currentBar.low = Math.min(currentBar.low, basePrice - movement);
          }

          currentBar.volume += volume;
          newData[newData.length - 1] = currentBar;
        } else {
          const lastClose = lastDataPoint.close;
          const newBar = {
            key: Date.now(),
            date: new Date(roundedTime),
            open: lastClose,
            high: lastClose,
            low: lastClose,
            close: lastClose,
            volume: volume,
            chart: [],
            indicators: [],
          };

          newBar.close = newPrice;
          newBar.high = Math.max(lastClose, newPrice);
          newBar.low = Math.min(lastClose, newPrice);
          newData.push(newBar);
          if (newData.length > 200) {
            newData.shift();
          }
        }

        return newData;
      });

      lastUpdateTimeRef.current = currentTime;
    },
    [liveData, setting?.period, currentPrice],
  );

  const checkAndCreateNewBar = useCallback(() => {
    if (!liveData || !Array.isArray(liveData) || liveData.length === 0) return;
    const period = setting?.period || "1m";
    const isLongTimeframe = [
      "1h",
      "4h",
      "1D",
      "1W",
      "1M",
      "hour",
      "day",
      "week",
      "month",
    ].includes(period);
    if (isLongTimeframe) {
      return;
    }

    const now = new Date();
    const currentTime = now.getTime();
    const multiplier = setting?.multiplier || 1;
    const interval = getTimeInterval(period, multiplier);
    const roundedTime = roundToInterval(currentTime, interval);

    const lastDataPoint = liveData[liveData.length - 1];
    const lastDataTime = new Date(lastDataPoint.date).getTime();
    const lastRoundedTime = roundToInterval(lastDataTime, interval);

    if (roundedTime > lastRoundedTime) {
      const lastClose = lastDataPoint.close;
      const newBar = createNewBar(currentTime, lastClose);

      setLiveData((prevData) => {
        const newData = [...prevData, newBar];
        if (newData.length > 200) {
          newData.shift();
        }
        return newData;
      });
    }
  }, [liveData, setting?.period, createNewBar]);

  useEffect(() => {
    if (!apiKey || !tickerFromLocalStorage) return;
    const wsPath = polygonCluster(tickerFromLocalStorage);
    const wsSymbol = polygonWsSymbol(tickerFromLocalStorage);

    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(
      `wss://business.polygon.io/${wsPath}?apiKey=${apiKey}`,
    );
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: "auth", params: apiKey }));
      const fmvSubscription = `FMV.${wsSymbol}`;
      ws.send(JSON.stringify({ action: "subscribe", params: fmvSubscription }));
      const tradeSubscription = `T.${wsSymbol}`;
      ws.send(
        JSON.stringify({ action: "subscribe", params: tradeSubscription }),
      );
      const quoteSubscription = `Q.${wsSymbol}`;
      ws.send(
        JSON.stringify({ action: "subscribe", params: quoteSubscription }),
      );
    };

    ws.onmessage = (event) => {
      try {
        const response = JSON.parse(event.data);
        if (
          response !== null &&
          response !== undefined &&
          Array.isArray(response) &&
          response.length > 0
        ) {
          response.forEach((msg) => {
            let price = null;

            if (msg && msg.ev === "FMV" && msg.sym === tickerFromLocalStorage) {
              price = msg.fmv;
            } else if (
              msg &&
              msg.ev === "T" &&
              msg.sym === tickerFromLocalStorage
            ) {
              price = msg.p;
            } else if (
              msg &&
              msg.ev === "Q" &&
              msg.sym === tickerFromLocalStorage
            ) {
              if (msg.bp && msg.ap) {
                price = (msg.bp + msg.ap) / 2;
              }
            }

            if (price && typeof price === "number" && price > 0) {
              updateLiveData(price, msg.v || msg.s || 0);
            }
          });
        } else {
          console.warn(
            "Received invalid WebSocket response (not an array or empty):",
            response,
          );
        }
      } catch (error) {
        console.error(
          "WebSocket message parsing error:",
          error,
          "Raw data:",
          event.data,
        );
      }
    };

    ws.onerror = (error) => {
      console.error("Live chart WebSocket error:", error);
    };

    ws.onclose = (event) => {
      console.warn("Live chart WebSocket closed:", event);
    };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [apiKey, tickerFromLocalStorage, updateLiveData]);

  useEffect(() => {
    const interval = getTimeInterval(
      setting?.period || "1m",
      setting?.multiplier || 1,
    );
    const checkInterval = Math.min(interval / 6, 10000);

    intervalRef.current = setInterval(() => {
      checkAndCreateNewBar();

      if (liveData && Array.isArray(liveData) && liveData.length > 0) {
        const lastBar = liveData[liveData.length - 1];
        const lastPrice = lastBar.close;
        const timeSinceLastUpdate =
          Date.now() - (lastUpdateTimeRef.current || 0);

        if (timeSinceLastUpdate > 30000) {
          const randomChange = (Math.random() - 0.5) * 0.002; // ±0.2% random change
          const simulatedPrice = lastPrice * (1 + randomChange);
          updateLiveData(simulatedPrice, Math.floor(Math.random() * 100) + 10);
        }
      }
    }, checkInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [setting?.period, checkAndCreateNewBar, liveData, updateLiveData]);

  useEffect(() => {
    if (initialData && Array.isArray(initialData) && initialData.length > 0) {
      setLiveData(initialData);
      lastUpdateTimeRef.current = new Date(
        initialData[initialData.length - 1]?.date,
      ).getTime();

      const lastPrice = initialData[initialData.length - 1]?.close;
      if (lastPrice && typeof lastPrice === "number") {
        setCurrentPrice(lastPrice);
        setPreviousPrice(lastPrice);
      }
    } else {
      setLiveData([]);
    }
  }, [initialData]);

  useEffect(() => {
    setLiveData([]);
    setCurrentPrice(null);
    setPreviousPrice(null);
    lastUpdateTimeRef.current = null;
  }, [tickerFromLocalStorage, setting?.period]);

  return {
    liveData,
    currentPrice,
    previousPrice,
  };
};

const LiveChart = () => {
  const [selectedSymbol, setSelectedSymbol] = useState("SPY");
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [setting, setSetting] = useState({
    period: "1m",
    multiplier: 1,
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0], // 24 hours ago
    symbol: "SPY",
  });

  const popularSymbols = [
    { label: "Apple", value: "AAPL" },
    { label: "Google", value: "GOOGL" },
    { label: "Microsoft", value: "MSFT" },
    { label: "Tesla", value: "TSLA" },
    { label: "Amazon", value: "AMZN" },
    { label: "Netflix", value: "NFLX" },
    { label: "Bitcoin", value: "X:BTCUSD" },
    { label: "Ethereum", value: "X:ETHUSD" },
    { label: "S&P 500", value: "SPY" },
    { label: "Dow Jones", value: "DIA" },
    { label: "EUR/USD", value: "C:EURUSD" },
    { label: "GBP/USD", value: "C:GBPUSD" },
  ];

  const timePeriods = [
    { label: "1m", value: "1m", multiplier: 1 },
    { label: "2m", value: "2m", multiplier: 2 },
    { label: "5m", value: "5m", multiplier: 5 },
    { label: "15m", value: "15m", multiplier: 15 },
    { label: "30m", value: "30m", multiplier: 30 },
    { label: "1h", value: "1h", multiplier: 1 },
    { label: "4h", value: "4h", multiplier: 4 },
    { label: "1D", value: "1D", multiplier: 1 },
    { label: "1W", value: "1W", multiplier: 1 },
    { label: "1M", value: "1M", multiplier: 1 },
  ];

  const { liveData, currentPrice, previousPrice } = useLiveChartData(
    selectedSymbol,
    setting,
    data,
  );

  const getApiSymbol = (symbol) => {
    if (symbol.startsWith("X:")) {
      return symbol;
    } else if (symbol.startsWith("C:")) {
      return symbol;
    } else {
      return symbol;
    }
  };

  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        setLoader(true);
        const apiSymbol = getApiSymbol(selectedSymbol);

        const category = apiSymbol.startsWith("C:")
          ? "Forex"
          : apiSymbol.startsWith("X:")
            ? "Crypto"
            : "Stocks";

        const candlestickParams = {
          symbol: apiSymbol,
          startDate: setting.startDate,
          period:
            setting.period === "1D"
              ? "day"
              : setting.period === "1W"
                ? "week"
                : setting.period === "1M"
                  ? "month"
                  : "minute",
          multiplier: setting.multiplier,
        };

        const response = await getCandlestickData(
          ...Object.values(candlestickParams),
        );

        if (
          response &&
          response.Date &&
          Array.isArray(response.Date) &&
          response.Date.length > 0
        ) {
          const chartData = response.Date.map((date, index) => ({
            key: index,
            date: new Date(date),
            open:
              response.Open && response.Open[index] ? response.Open[index] : 0,
            high:
              response.High && response.High[index] ? response.High[index] : 0,
            low: response.Low && response.Low[index] ? response.Low[index] : 0,
            close:
              response.Close && response.Close[index]
                ? response.Close[index]
                : 0,
            volume:
              response.Volume && response.Volume[index]
                ? response.Volume[index]
                : 0,
            chart: [],
            indicators: [],
          }));

          setData(chartData);
        } else {
          console.warn("Invalid or empty historical data response:", response);
          setData([]);
        }
      } catch (error) {
        console.error("Error fetching historical data:", error);
        toast.error("Could not load older chart prices. Try again.");
        setData([]);
      } finally {
        setLoader(false);
      }
    };

    fetchHistoricalData();
  }, [selectedSymbol, setting.period, setting.multiplier]);

  const handleSymbolChange = (newSymbol) => {
    setSelectedSymbol(newSymbol);
    setSetting((prev) => ({
      ...prev,
      symbol: newSymbol,
    }));
  };

  const handlePeriodChange = (newPeriod) => {
    const periodConfig = timePeriods.find((p) => p.value === newPeriod);
    if (periodConfig) {
      setSetting((prev) => ({
        ...prev,
        period: periodConfig.value,
        multiplier: periodConfig.multiplier,
      }));
    }
  };

  return (
    <>
      <DashboardHeader />

      <PageHeaderTitle title="Live Charts" />

      <PageContainer>
        <div className="bg-white rounded-lg shadow-lg overflow-visible">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {popularSymbols.find((s) => s.value === selectedSymbol)
                    ?.label || selectedSymbol}
                </h2>
                <p className="text-sm text-gray-600">
                  Real-time Interactive Chart
                </p>
              </div>

              <div className="flex items-center space-x-4 flex-wrap gap-2">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">
                    Symbol:
                  </label>
                  <select
                    value={selectedSymbol}
                    onChange={(e) => handleSymbolChange(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {popularSymbols.map((symbol) => (
                      <option key={symbol.value} value={symbol.value}>
                        {symbol.label} ({symbol.value})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">
                    Period:
                  </label>
                  <select
                    value={setting.period}
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {timePeriods.map((period) => (
                      <option key={period.value} value={period.value}>
                        {period.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Live Price Display */}
          {currentPrice && (
            <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl font-bold text-gray-900">
                      ${currentPrice.toFixed(2)}
                    </div>
                    {previousPrice && (
                      <div
                        className={`flex items-center space-x-1 px-2 py-1 rounded-full text-sm font-medium ${
                          currentPrice >= previousPrice
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        <span>{currentPrice >= previousPrice ? "↗" : "↘"}</span>
                        <span>{(currentPrice - previousPrice).toFixed(2)}</span>
                        <span>
                          (
                          {previousPrice !== 0
                            ? (
                                ((currentPrice - previousPrice) /
                                  previousPrice) *
                                100
                              ).toFixed(2)
                            : 0}
                          %)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Live</span>
                  </div>
                  <div className="text-xs">
                    Last update: {new Date().toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="p-6" style={{ minHeight: "650px" }}>
            {loader ? (
              <SkeletonChart height={600} />
            ) : liveData && Array.isArray(liveData) && liveData.length > 0 ? (
              <div className="w-full h-full border border-gray-200 rounded-lg overflow-hidden">
                <ChartComp
                  initialData={liveData}
                  setting={{
                    ...setting,
                    chartType: "candlestick",
                    enablePrice: true,
                    quote: true,
                    chartOverlay: "on",
                    colorMode: "light",
                  }}
                  indicatorConfig={[]}
                  drawingCanva={null}
                  gridSetting={{
                    style: "Solid",
                    opacity: "0.3",
                    strokeWidth: "1",
                    grid: true,
                  }}
                  setSetting={() => {}}
                  setIndicatorConfig={() => {}}
                  symbol={selectedSymbol}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 border border-gray-200 rounded-lg">
                <div className="text-center">
                  <p className="text-gray-500 mb-2">No chart data available</p>
                  <p className="text-sm text-gray-400">
                    Try selecting a different symbol or time period
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📈 Real-time Data
            </h3>
            <p className="text-gray-600 text-sm">
              Live market data with professional-grade charts powered by
              Polygon.io WebSocket API.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              ⚡ Live Updates
            </h3>
            <p className="text-gray-600 text-sm">
              Charts update in real-time with live price movements. New bars are
              created automatically at time intervals.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🎯 Multiple Assets
            </h3>
            <p className="text-gray-600 text-sm">
              Track stocks, crypto, forex, and indices all in one place with
              easy symbol switching and time period selection.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border-l-4 border-orange-500">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📊 Professional Charts
            </h3>
            <p className="text-gray-600 text-sm">
              Advanced candlestick charts with OHLC tooltips, volume bars, and
              TradingView-style appearance.
            </p>
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default LiveChart;
